<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  createBrandedPng,
  createBrandedSvg,
} from 'dashboard/helpers/whatsappLinkQrCode';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import whatsappLinksAPI from 'dashboard/api/whatsappLinks';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const links = ref([]);
const selectedLink = ref(null);
const qrCodePreview = ref('');
const isLoading = ref(false);
const isSaving = ref(false);

const form = reactive({
  id: null,
  name: '',
  phone_number: '',
  message: '',
  enabled: true,
});

const defaultPhoneNumber = computed(
  () => props.inbox.phone_number?.replace(/^whatsapp:/i, '') || ''
);

const editing = computed(() => form.id !== null);
const formTitle = computed(() =>
  editing.value
    ? t('INBOX_MGMT.WHATSAPP_LINKS.EDIT_TITLE')
    : t('INBOX_MGMT.WHATSAPP_LINKS.CREATE_TITLE')
);

const errorMessage = error =>
  error?.response?.data?.error || t('INBOX_MGMT.WHATSAPP_LINKS.API.ERROR');

const resetForm = () => {
  form.id = null;
  form.name = '';
  form.phone_number = defaultPhoneNumber.value;
  form.message = '';
  form.enabled = true;
};

const openEditor = link => {
  form.id = link.id;
  form.name = link.name;
  form.phone_number = link.phone_number;
  form.message = link.message || '';
  form.enabled = link.enabled;
};

const openQRCode = async link => {
  selectedLink.value = link;
  qrCodePreview.value = await createBrandedPng(link.public_url, 320);
};

const fetchLinks = async () => {
  isLoading.value = true;
  try {
    const { data } = await whatsappLinksAPI.index(props.inbox.id);
    links.value = data;
    if (selectedLink.value) {
      const refreshedLink = data.find(
        link => link.id === selectedLink.value.id
      );
      if (refreshedLink) await openQRCode(refreshedLink);
    }
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isLoading.value = false;
  }
};

const save = async () => {
  isSaving.value = true;
  try {
    const payload = {
      name: form.name,
      phone_number: form.phone_number,
      message: form.message,
      enabled: form.enabled,
    };
    const { data } = editing.value
      ? await whatsappLinksAPI.update(props.inbox.id, form.id, payload)
      : await whatsappLinksAPI.create(props.inbox.id, payload);

    const index = links.value.findIndex(link => link.id === data.id);
    if (index === -1) links.value = [data, ...links.value];
    else links.value.splice(index, 1, data);

    await openQRCode(data);
    resetForm();
    useAlert(t('INBOX_MGMT.WHATSAPP_LINKS.API.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isSaving.value = false;
  }
};

const toggleLink = async link => {
  try {
    const { data } = await whatsappLinksAPI.update(props.inbox.id, link.id, {
      name: link.name,
      phone_number: link.phone_number,
      message: link.message,
      enabled: !link.enabled,
    });
    const index = links.value.findIndex(item => item.id === link.id);
    links.value.splice(index, 1, data);
    if (selectedLink.value?.id === data.id) await openQRCode(data);
  } catch (error) {
    useAlert(errorMessage(error));
  }
};

const copy = async value => {
  await copyTextToClipboard(value);
  useAlert(t('INBOX_MGMT.WHATSAPP_LINKS.API.COPY_SUCCESS'));
};

const fileName = extension => {
  const safeName = (selectedLink.value?.name || 'whatsapp-link')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
  return `${safeName || 'whatsapp-link'}.qr.${extension}`;
};

const downloadSVG = async () => {
  const svg = await createBrandedSvg(selectedLink.value.public_url);

  const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
  const href = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = fileName('svg');
  link.click();
  URL.revokeObjectURL(href);
};

const exactPngDataUrl = async value => {
  return createBrandedPng(value, 2048);
};

const downloadPNG = async () => {
  const href = await exactPngDataUrl(selectedLink.value.public_url);
  const link = document.createElement('a');
  link.href = href;
  link.download = fileName('png');
  link.click();
};

onMounted(() => {
  resetForm();
  fetchLinks();
});
</script>

<template>
  <section class="flex flex-col gap-6">
    <div>
      <h2 class="text-heading-3 text-n-slate-12">
        {{ $t('INBOX_MGMT.WHATSAPP_LINKS.TITLE') }}
      </h2>
      <p class="mt-1 text-body-main text-n-slate-10">
        {{ $t('INBOX_MGMT.WHATSAPP_LINKS.DESCRIPTION') }}
      </p>
    </div>

    <form class="grid gap-4 max-w-3xl" @submit.prevent="save">
      <h3 class="text-heading-4 text-n-slate-12">{{ formTitle }}</h3>
      <label class="flex flex-col gap-1 text-body-main text-n-slate-12">
        {{ $t('INBOX_MGMT.WHATSAPP_LINKS.FIELDS.NAME') }}
        <input
          v-model="form.name"
          required
          class="rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-2 outline-none focus:border-n-brand"
          :placeholder="$t('INBOX_MGMT.WHATSAPP_LINKS.PLACEHOLDERS.NAME')"
        />
      </label>
      <label class="flex flex-col gap-1 text-body-main text-n-slate-12">
        {{ $t('INBOX_MGMT.WHATSAPP_LINKS.FIELDS.PHONE') }}
        <input
          v-model="form.phone_number"
          required
          inputmode="tel"
          class="rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-2 outline-none focus:border-n-brand"
          :placeholder="$t('INBOX_MGMT.WHATSAPP_LINKS.PLACEHOLDERS.PHONE')"
        />
      </label>
      <label class="flex flex-col gap-1 text-body-main text-n-slate-12">
        {{ $t('INBOX_MGMT.WHATSAPP_LINKS.FIELDS.MESSAGE') }}
        <textarea
          v-model="form.message"
          rows="4"
          class="rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-2 outline-none focus:border-n-brand"
          :placeholder="$t('INBOX_MGMT.WHATSAPP_LINKS.PLACEHOLDERS.MESSAGE')"
        />
      </label>
      <div class="flex gap-2">
        <NextButton
          solid
          blue
          type="submit"
          :is-loading="isSaving"
          :label="
            editing
              ? $t('INBOX_MGMT.WHATSAPP_LINKS.UPDATE')
              : $t('INBOX_MGMT.WHATSAPP_LINKS.CREATE')
          "
        />
        <NextButton
          v-if="editing"
          outline
          slate
          type="button"
          :label="$t('INBOX_MGMT.WHATSAPP_LINKS.CANCEL')"
          @click="resetForm"
        />
      </div>
    </form>

    <div class="flex flex-col gap-3">
      <p v-if="isLoading" class="text-body-main text-n-slate-10">
        {{ $t('INBOX_MGMT.WHATSAPP_LINKS.LOADING') }}
      </p>
      <p v-else-if="!links.length" class="text-body-main text-n-slate-10">
        {{ $t('INBOX_MGMT.WHATSAPP_LINKS.EMPTY') }}
      </p>
      <article
        v-for="link in links"
        :key="link.id"
        class="flex flex-col gap-3 rounded-xl border border-n-weak bg-n-surface-1 p-4"
      >
        <div class="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 class="text-body-main font-medium text-n-slate-12">
              {{ link.name }}
            </h3>
            <p class="text-body-mini text-n-slate-10">
              {{ link.phone_number }}
            </p>
          </div>
          <span
            class="rounded-full px-2 py-1 text-body-mini"
            :class="
              link.enabled
                ? 'bg-n-teal-3 text-n-teal-11'
                : 'bg-n-slate-3 text-n-slate-11'
            "
          >
            {{
              link.enabled
                ? $t('INBOX_MGMT.WHATSAPP_LINKS.ENABLED')
                : $t('INBOX_MGMT.WHATSAPP_LINKS.DISABLED')
            }}
          </span>
        </div>
        <code
          class="break-all rounded bg-n-alpha-2 p-2 text-body-mini text-n-slate-11"
        >
          {{ link.public_url }}
        </code>
        <div class="flex flex-wrap gap-2">
          <NextButton
            outline
            slate
            :label="$t('INBOX_MGMT.WHATSAPP_LINKS.EDIT')"
            @click="openEditor(link)"
          />
          <NextButton
            outline
            slate
            :label="$t('INBOX_MGMT.WHATSAPP_LINKS.QR_CODE')"
            @click="openQRCode(link)"
          />
          <NextButton
            outline
            slate
            :label="
              link.enabled
                ? $t('INBOX_MGMT.WHATSAPP_LINKS.DEACTIVATE')
                : $t('INBOX_MGMT.WHATSAPP_LINKS.REACTIVATE')
            "
            @click="toggleLink(link)"
          />
        </div>
      </article>
    </div>

    <div
      v-if="selectedLink && qrCodePreview"
      class="flex flex-col gap-4 rounded-xl border border-n-weak p-4"
    >
      <h3 class="text-heading-4 text-n-slate-12">{{ selectedLink.name }}</h3>
      <img
        :src="qrCodePreview"
        :alt="$t('INBOX_MGMT.WHATSAPP_LINKS.QR_ALT')"
        class="size-80 max-w-full bg-white p-4"
      />
      <div class="flex flex-wrap gap-2">
        <NextButton
          outline
          slate
          :label="$t('INBOX_MGMT.WHATSAPP_LINKS.COPY_PERMANENT')"
          @click="copy(selectedLink.public_url)"
        />
        <a
          :href="selectedLink.public_url"
          target="_blank"
          rel="noopener noreferrer"
        >
          <NextButton
            outline
            slate
            :label="$t('INBOX_MGMT.WHATSAPP_LINKS.TEST_LINK')"
          />
        </a>
        <NextButton
          outline
          slate
          :label="$t('INBOX_MGMT.WHATSAPP_LINKS.COPY_WHATSAPP')"
          @click="copy(selectedLink.whatsapp_url)"
        />
        <NextButton
          solid
          blue
          :label="$t('INBOX_MGMT.WHATSAPP_LINKS.DOWNLOAD_SVG')"
          @click="downloadSVG"
        />
        <NextButton
          solid
          blue
          :label="$t('INBOX_MGMT.WHATSAPP_LINKS.DOWNLOAD_PNG')"
          @click="downloadPNG"
        />
      </div>
    </div>
  </section>
</template>
